import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addWeeks, subWeeks, format, getISOWeek, addDays } from 'date-fns'
import { it as itLocale, enUS } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, CalendarDays, CalendarCog, Target } from 'lucide-react'
import { Button } from '../ui/button'
import { MonthPickerSheet } from './MonthPickerSheet'
import type { CalendarDaySummary } from '../../services/training-diary'

interface WeekNavigatorProps {
  weekStart: Date
  onWeekChange: (date: Date) => void
  onAddGoal: () => void
  getCalendarSummary: (from: string, to: string) => Promise<Record<string, CalendarDaySummary>>
  scheduleMode: boolean
  scheduleHasChanges: boolean
  scheduleSaving: boolean
  onEnterScheduleMode: () => void
  onSaveSchedule: () => void
  onCancelSchedule: () => void
}

export function WeekNavigator({
  weekStart,
  onWeekChange,
  onAddGoal,
  getCalendarSummary,
  scheduleMode,
  scheduleHasChanges,
  scheduleSaving,
  onEnterScheduleMode,
  onSaveSchedule,
  onCancelSchedule,
}: WeekNavigatorProps) {
  const { t, i18n } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)

  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS

  const weekEnd = addDays(weekStart, 6)
  const weekNum = getISOWeek(weekStart)
  const rangeLabel = `${format(weekStart, 'd MMM', { locale: dateFnsLocale })} – ${format(weekEnd, 'd MMM yyyy', { locale: dateFnsLocale })}`
  const weekLabel = t('trainingDiary.weekLabel', { week: weekNum, range: rangeLabel })

  if (scheduleMode) {
    return (
      <div className="sticky top-16 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 bg-background border-b flex items-center justify-between gap-2 py-2">
        <span className="text-sm font-medium">{weekLabel}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={onCancelSchedule}
            disabled={scheduleSaving}
          >
            {t('trainingDiary.scheduleMode.cancel', 'Cancel')}
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={onSaveSchedule}
            disabled={!scheduleHasChanges || scheduleSaving}
          >
            {scheduleSaving
              ? t('common.saving', 'Saving…')
              : t('trainingDiary.scheduleMode.save', 'Save')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="sticky top-16 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 bg-background border-b flex items-center justify-between gap-2 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onWeekChange(subWeeks(weekStart, 1))}
          aria-label={t('trainingDiary.prevWeek')}
        >
          <ChevronLeft className="size-5" />
        </Button>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{weekLabel}</span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setPickerOpen(true)}
            aria-label={t('trainingDiary.pickWeek')}
          >
            <CalendarDays className="size-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          {/* Goal button */}
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onAddGoal}
            aria-label={t('trainingDiary.goals.add')}
          >
            <Target className="size-4" />
          </Button>

          {/* Schedule mode toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onEnterScheduleMode}
            aria-label={t('trainingDiary.scheduleMode.enable', 'Schedule workouts')}
          >
            <CalendarCog className="size-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => onWeekChange(addWeeks(weekStart, 1))}
            aria-label={t('trainingDiary.nextWeek')}
          >
            <ChevronRight className="size-5" />
          </Button>
        </div>
      </div>

      <MonthPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onDaySelect={onWeekChange}
        getCalendarSummary={getCalendarSummary}
      />
    </>
  )
}
